import {
  canvasFontCss,
  isItalicTextStyle,
  letterSpacingPx,
  runBoundarySpacing,
  measureTextWidthWithLetterSpacing,
  strokeWidthPx,
} from "../utils/textStyle";
import {
  hasAnyPaintStyle,
  listActiveHighlights,
  listWordHighlightRuns,
  resolveHighlightCharStyles,
} from "../utils/textHighlights";
import {
  capHeightFromMetrics,
  imageRunSize,
  isInlineImageRun,
  listActiveInlineReplacements,
  listLineInlineRuns,
  wrapUnitsFromParagraph,
} from "../utils/textInlineReplacements";
import {
  isVerticalTextOrientation,
  resolveTextOrientation,
} from "../utils/textOrientation";

const MIN_FONT_SIZE_PX = 8;
const DEFAULT_LINE_HEIGHT_MULT = 1.2;
const DEFAULT_VERTICAL_LINE_HEIGHT_MULT = 1;

let measureCtx = null;

const getMeasureCtx = () => {
  if (measureCtx) return measureCtx;
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  measureCtx = canvas.getContext("2d");
  return measureCtx;
};

const setCanvasFont = (ctx, fontSize, fontFamily, italic, fontWeight) => {
  ctx.font = canvasFontCss({ fontSize, fontFamily, italic, fontWeight });
};

const identityStyle = (style) => style;

// A line's vertical extent comes from this glyph, not from the line's own ink:
// accents and lowercase used to move the baseline from one text to the next,
// and Chrome rounds the flat descent of an all-caps line to 0, which the old
// `|| 0.22em` fallback turned into phantom space under the text. Descenders
// may hang below the box.
const LINE_EXTENT_REFERENCE = "H";

// The reference is measured at this size and scaled down: Chrome rounds glyph
// bounds to whole pixels, so read at the fitted size it came up to half a pixel
// short and the text sat that much high in its box.
const REFERENCE_MEASURE_PX = 1000;
const referenceAscentCache = new Map();

// A ratio read off a fallback face is wrong for the real one, so it is only
// kept once the font has loaded (always, outside the browser).
const isFontResolved = (font) => {
  if (typeof document === "undefined" || !document.fonts?.check) return true;
  try {
    return document.fonts.check(font);
  } catch (_error) {
    return true;
  }
};

const measureReferenceAscent = (
  ctx,
  fontSize,
  fontFamily,
  italic,
  fontWeight
) => {
  setCanvasFont(ctx, REFERENCE_MEASURE_PX, fontFamily, italic, fontWeight);
  const key = ctx.font;
  let ratio = referenceAscentCache.get(key);
  if (ratio == null) {
    const ascent = ctx.measureText(
      LINE_EXTENT_REFERENCE
    ).actualBoundingBoxAscent;
    ratio = ascent != null ? ascent / REFERENCE_MEASURE_PX : 0.78;
    if (isFontResolved(key)) referenceAscentCache.set(key, ratio);
  }
  setCanvasFont(ctx, fontSize, fontFamily, italic, fontWeight);
  return ratio * fontSize;
};

const runStrokeWidth = (style, baseStroke) =>
  style?.stroke != null ? strokeWidthPx(style.strokeWidth) : baseStroke;

const measureLine = (
  ctx,
  text,
  fontSize,
  fontFamily,
  italic,
  letterSpacing,
  fontWeight
) => {
  setCanvasFont(ctx, fontSize, fontFamily, italic, fontWeight);
  const ascent = measureReferenceAscent(
    ctx,
    fontSize,
    fontFamily,
    italic,
    fontWeight
  );
  const descent = 0;
  return {
    width: measureTextWidthWithLetterSpacing(ctx, text, letterSpacing),
    ascent,
    descent,
    height: ascent + descent,
  };
};

const measureCapHeight = (ctx, fontSize, fontFamily, italic, fontWeight) => {
  setCanvasFont(ctx, fontSize, fontFamily, italic, fontWeight);
  return capHeightFromMetrics(ctx.measureText("H"), fontSize);
};

const measureRuns = ({
  ctx,
  runs,
  fontSize,
  fontFamily,
  italic,
  letterSpacing,
  fontWeight,
  strokeWidth = 0,
  resolveRunStyle = identityStyle,
}) => {
  const list =
    Array.isArray(runs) && runs.length ? runs : [{ text: "", style: null }];
  let width = 0;
  let ascent = 0;
  let descent = 0;
  let inkPad = strokeWidthPx(strokeWidth);
  const capHeight = measureCapHeight(
    ctx,
    fontSize,
    fontFamily,
    italic,
    fontWeight
  );
  list.forEach((run, index) => {
    width += runBoundarySpacing(letterSpacing, index);
    if (isInlineImageRun(run)) {
      const size = imageRunSize({ capHeight, aspect: run.aspect });
      width += size.width;
      ascent = Math.max(ascent, size.height || capHeight);
      return;
    }
    const style = resolveRunStyle(run?.style) || null;
    const family = style?.fontFamily || fontFamily;
    const weight =
      Number(style?.fontWeight) > 0 ? Number(style.fontWeight) : fontWeight;
    const runItalic =
      style?.textStyle != null ? isItalicTextStyle(style.textStyle) : italic;
    inkPad = Math.max(
      inkPad,
      runStrokeWidth(style, strokeWidthPx(strokeWidth))
    );
    const metrics = measureLine(
      ctx,
      run?.text || "",
      fontSize,
      family,
      runItalic,
      letterSpacing,
      weight
    );
    width += metrics.width;
    ascent = Math.max(ascent, metrics.ascent);
    descent = Math.max(descent, metrics.descent);
  });
  return {
    width: width + inkPad,
    ascent,
    descent,
    height: ascent + descent,
  };
};

const runsFromWrapUnits = ({ units = [], charStyles = null }) => {
  const styled = Array.isArray(charStyles) && hasAnyPaintStyle(charStyles);
  const runs = [];
  units.forEach((unit, index) => {
    if (index > 0) runs.push({ text: " ", style: null });
    if (isInlineImageRun(unit)) {
      runs.push({
        kind: unit.kind,
        text: unit.text,
        phrase: unit.phrase,
        assetRef: unit.assetRef,
        aspect: unit.aspect,
        style: null,
      });
      return;
    }
    if (styled) {
      runs.push(
        ...listWordHighlightRuns({
          words: [{ text: unit.text, start: unit.start, end: unit.end }],
          charStyles,
        })
      );
      return;
    }
    runs.push({ text: unit.text, style: null });
  });
  return runs;
};

const wrapTextToWidth = ({
  ctx,
  text,
  fontSize,
  fontFamily,
  maxWidth,
  italic,
  letterSpacing,
  fontWeight,
  charStyles = null,
  strokeWidth = 0,
  resolveRunStyle = identityStyle,
  inlineReplacements,
  replacementMetrics,
}) => {
  const styled = Array.isArray(charStyles) && hasAnyPaintStyle(charStyles);
  const hasInline =
    listActiveInlineReplacements(inlineReplacements, replacementMetrics)
      .length > 0;
  if (!styled && !hasInline) {
    const paragraphs = String(text || "").split(/\n/);
    const lines = [];

    paragraphs.forEach((paragraph) => {
      const words = paragraph.split(/\s+/).filter(Boolean);
      if (words.length === 0) {
        lines.push("");
        return;
      }

      let current = words[0];
      for (let index = 1; index < words.length; index += 1) {
        const trial = `${current} ${words[index]}`;
        const { width } = measureLine(
          ctx,
          trial,
          fontSize,
          fontFamily,
          italic,
          letterSpacing,
          fontWeight
        );
        if (width + strokeWidthPx(strokeWidth) <= maxWidth) {
          current = trial;
        } else {
          lines.push(current);
          current = words[index];
        }
      }
      lines.push(current);
    });

    return lines.length > 0 ? lines : [""];
  }

  const lines = [];
  const paragraphs = String(text || "").split(/\n/);
  let paragraphStart = 0;
  paragraphs.forEach((paragraph, paragraphIndex) => {
    const units = wrapUnitsFromParagraph({
      paragraph,
      paragraphStart,
      replacements: inlineReplacements,
      metrics: replacementMetrics,
    });
    paragraphStart +=
      paragraph.length + (paragraphIndex < paragraphs.length - 1 ? 1 : 0);
    if (units.length === 0) {
      lines.push("");
      return;
    }

    let current = [units[0]];
    for (let index = 1; index < units.length; index += 1) {
      const trial = current.concat(units[index]);
      const { width } = measureRuns({
        ctx,
        runs: runsFromWrapUnits({ units: trial, charStyles }),
        fontSize,
        fontFamily,
        italic,
        letterSpacing,
        fontWeight,
        strokeWidth,
        resolveRunStyle,
      });
      if (width <= maxWidth) {
        current = trial;
      } else {
        lines.push(current.map((unit) => unit.text).join(" "));
        current = [units[index]];
      }
    }
    lines.push(current.map((unit) => unit.text).join(" "));
  });

  return lines.length > 0 ? lines : [""];
};

const wrapTextVertical = ({ text }) => {
  const chars = Array.from(String(text || "")).filter(
    (char) => !/\s/.test(char)
  );
  return chars.length > 0 ? chars : [""];
};

const wrapLines = ({
  ctx,
  text,
  fontSize,
  fontFamily,
  maxWidth,
  orientation,
  italic,
  letterSpacing,
  fontWeight,
  charStyles,
  strokeWidth,
  resolveRunStyle,
  inlineReplacements,
  replacementMetrics,
}) => {
  if (isVerticalTextOrientation(orientation)) {
    return wrapTextVertical({ text });
  }
  return wrapTextToWidth({
    ctx,
    text,
    fontSize,
    fontFamily,
    maxWidth,
    italic,
    letterSpacing,
    fontWeight,
    charStyles,
    strokeWidth,
    resolveRunStyle,
    inlineReplacements,
    replacementMetrics,
  });
};

const measureBlock = ({
  ctx,
  text,
  lines,
  fontSize,
  fontFamily,
  lineHeightMult,
  italic,
  letterSpacing,
  orientation,
  underline = false,
  fontWeight,
  highlights,
  phrases,
  strokeWidth = 0,
  resolveRunStyle = identityStyle,
  inlineReplacements,
  replacementMetrics,
}) => {
  const spacing = letterSpacingPx(letterSpacing);
  const lineRuns = listLineInlineRuns({
    text,
    lines,
    highlights,
    phrases,
    replacements: inlineReplacements,
    metrics: replacementMetrics,
    orientation,
  });
  const lineMetrics = lines.map((line, index) => {
    const runs = lineRuns?.[index];
    if (runs) {
      return measureRuns({
        ctx,
        runs,
        fontSize,
        fontFamily,
        italic,
        letterSpacing: spacing,
        fontWeight,
        strokeWidth,
        resolveRunStyle,
      });
    }
    const metrics = measureLine(
      ctx,
      line,
      fontSize,
      fontFamily,
      italic,
      spacing,
      fontWeight
    );
    const inkPad = strokeWidthPx(strokeWidth);
    return {
      ...metrics,
      width: metrics.width + inkPad,
    };
  });
  const maxWidth = lineMetrics.reduce(
    (max, line) => Math.max(max, line.width),
    0
  );
  const firstAscent = lineMetrics[0]?.ascent ?? fontSize * 0.78;
  const lastDescent = lineMetrics[lineMetrics.length - 1]?.descent ?? 0;
  const lineAdvance =
    fontSize * lineHeightMult +
    (isVerticalTextOrientation(orientation) ? spacing : 0);
  const underlinePad = underline ? Math.max(1, Math.round(fontSize * 0.16)) : 0;
  const totalHeight =
    (lines.length <= 1
      ? firstAscent + lastDescent
      : (lines.length - 1) * lineAdvance + firstAscent + lastDescent) +
    underlinePad;

  return {
    maxWidth,
    totalHeight,
    firstAscent,
    lastDescent,
    lineAdvance,
  };
};

const estimateMaxFontSize = ({
  boxWidth,
  boxHeight,
  minLines,
  lineHeightMult,
}) => {
  const lineCount = Math.max(1, minLines);
  const heightFactor =
    lineCount <= 1 ? 1 : (lineCount - 1) * lineHeightMult + 1;
  const byHeight = boxHeight / heightFactor;
  return Math.max(MIN_FONT_SIZE_PX, Math.ceil(Math.min(byHeight, boxWidth)));
};

const parsePositiveLineCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
};

const layoutFits = ({
  metrics,
  boxWidth,
  boxHeight,
  lineCount,
  maxLines,
  orientation,
}) => {
  if (metrics.maxWidth > boxWidth || metrics.totalHeight > boxHeight) {
    return false;
  }
  if (isVerticalTextOrientation(orientation) || maxLines == null) return true;
  return lineCount <= maxLines;
};

const resolveFitCharStyles = ({ text, highlights, phrases }) => {
  const active = listActiveHighlights(highlights);
  if (!active.length) return null;
  const charStyles = resolveHighlightCharStyles({
    text,
    highlights: active,
    phrases,
  });
  return hasAnyPaintStyle(charStyles) ? charStyles : null;
};

/** requestedFontSize is a cap: use it if the copy fits, otherwise shrink. */
export const fitTextInBox = ({
  ctx: ctxArg,
  text,
  boxWidth,
  boxHeight,
  fontFamily,
  minimumLines,
  maximumLines,
  minimumFontSize,
  requestedFontSize,
  lineHeightMult,
  letterSpacing: letterSpacingArg,
  orientation: orientationArg,
  italic = false,
  underline = false,
  fontWeight = 400,
  highlights,
  phrases,
  strokeWidth: strokeWidthArg = 0,
  resolveRunStyle = identityStyle,
  inlineReplacements,
  replacementMetrics,
}) => {
  const ctx = ctxArg || getMeasureCtx();
  if (!ctx) {
    return { ok: false, reason: "noCanvas" };
  }
  const orientation = resolveTextOrientation(orientationArg);
  const minLines = parsePositiveLineCount(minimumLines);
  let maxLines = parsePositiveLineCount(maximumLines);
  if (minLines != null && maxLines != null) {
    maxLines = Math.max(minLines, maxLines);
  }
  const minSize = isVerticalTextOrientation(orientation)
    ? Math.max(1, Number(minimumFontSize) || 1)
    : Math.max(MIN_FONT_SIZE_PX, Number(minimumFontSize) || MIN_FONT_SIZE_PX);
  const parsedLineHeight = Number(lineHeightMult);
  const lineHeight =
    Number.isFinite(parsedLineHeight) && parsedLineHeight >= 0
      ? parsedLineHeight
      : isVerticalTextOrientation(orientation)
        ? DEFAULT_VERTICAL_LINE_HEIGHT_MULT
        : DEFAULT_LINE_HEIGHT_MULT;
  const letterSpacing = letterSpacingPx(letterSpacingArg);
  const strokeWidth = strokeWidthPx(strokeWidthArg);
  const charStyles = resolveFitCharStyles({ text, highlights, phrases });

  if (!String(text || "").trim() || boxWidth <= 0 || boxHeight <= 0) {
    return { ok: false, reason: "emptyTextOrBox" };
  }

  const requested = Number(requestedFontSize);
  const hasRequested = Number.isFinite(requested) && requested > 0;
  const verticalLines = isVerticalTextOrientation(orientation)
    ? wrapTextVertical({ text })
    : null;
  let low = minSize;
  let high = estimateMaxFontSize({
    boxWidth,
    boxHeight,
    minLines: verticalLines ? verticalLines.length : minLines || 1,
    lineHeightMult: lineHeight,
  });
  if (hasRequested) {
    const requestedSize = Math.max(1, Math.round(requested));
    high = requestedSize;
    low = Math.min(minSize, requestedSize);
  } else if (high < minSize) {
    high = minSize;
  }
  let best = null;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const lines = wrapLines({
      ctx,
      text,
      fontSize: mid,
      fontFamily,
      maxWidth: boxWidth,
      orientation,
      italic,
      letterSpacing,
      fontWeight,
      charStyles,
      strokeWidth,
      resolveRunStyle,
      inlineReplacements,
      replacementMetrics,
    });
    const metrics = measureBlock({
      ctx,
      text,
      lines,
      fontSize: mid,
      fontFamily,
      lineHeightMult: lineHeight,
      italic,
      letterSpacing,
      orientation,
      underline,
      fontWeight,
      highlights,
      phrases,
      strokeWidth,
      resolveRunStyle,
      inlineReplacements,
      replacementMetrics,
    });

    if (
      layoutFits({
        metrics,
        boxWidth,
        boxHeight,
        lineCount: lines.length,
        maxLines,
        orientation,
      })
    ) {
      best = {
        fontSize: mid,
        lines,
        metrics,
        lineHeightMult: lineHeight,
        letterSpacing,
      };
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  if (!best) {
    return { ok: false, reason: "textOverflow" };
  }

  return { ok: true, ...best };
};

/**
 * Font ascent/descent of the CSS strut, the box a line reserves before any
 * glyph is measured. measureBlock works from actualBoundingBox (ink), so the
 * two disagree by the leading the font declares; the editor needs the strut to
 * land its first baseline where the fitted paint puts it.
 */
export const measureFontStrut = ({
  fontSize,
  fontFamily,
  italic = false,
  fontWeight = 400,
}) => {
  const size = Number(fontSize) > 0 ? Number(fontSize) : 0;
  const fallback = { ascent: size * 0.8, descent: size * 0.2 };
  const ctx = getMeasureCtx();
  if (!ctx || size <= 0) return fallback;
  setCanvasFont(ctx, size, fontFamily, italic, fontWeight);
  const metrics = ctx.measureText("Hg");
  const ascent = metrics.fontBoundingBoxAscent;
  const descent = metrics.fontBoundingBoxDescent;
  if (!Number.isFinite(ascent) || !Number.isFinite(descent)) return fallback;
  return { ascent, descent };
};

export { wrapTextToWidth, wrapTextVertical, MIN_FONT_SIZE_PX };
